import { useMemo, useState } from "react";
import { ContentHeader } from "../components/ContentHeader";
import { useApi } from "../hooks/useApi";
import { useData } from "../data/DataContext";

// Response shapes are defined locally (mirrors upstreams/loki.py); no api/types import.
interface LogLine {
  ts: string;            // nanosecond epoch as a string
  line: string;
  labels: Record<string, string>;
}
interface RecentLogsResponse {
  status: "ok" | "unavailable" | "error";
  reason?: string;
  lines: LogLine[];
}
interface ErrorRateRow {
  app: string;
  errors_1h: number;
}
interface ErrorRatesResponse {
  status: "ok" | "unavailable" | "error";
  reason?: string;
  errors_1h?: number;
  top_apps?: ErrorRateRow[];
}

const LEVELS = ["ERROR", "WARN", "INFO"] as const;
const HOURS = [
  { value: 1, label: "1h" },
  { value: 6, label: "6h" },
  { value: 24, label: "24h" },
];

function fmtTs(ns: string): string {
  // Loki timestamps are nanosecond strings → ms for Date.
  const ms = Number(ns) / 1_000_000;
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleTimeString();
}

function lineColor(line: string): string | undefined {
  if (line.includes("ERROR")) return "var(--crit, #e5484d)";
  if (line.includes("WARN")) return "var(--warn, #f5a623)";
  return undefined;
}

export function LogsTab() {
  const { commandCenter } = useData();
  const [service, setService] = useState<string>("");
  const [level, setLevel] = useState<string>("");
  const [hours, setHours] = useState<number>(1);

  const services = useMemo(() => {
    const names = [
      ...(commandCenter?.estates.backend ?? []),
      ...(commandCenter?.estates.surfaces ?? []),
    ].map((e) => e.name);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [commandCenter]);

  const recentPath = useMemo(() => {
    const qs = new URLSearchParams();
    if (service) qs.set("service", service);
    if (level) qs.set("level", level);
    qs.set("hours", String(hours));
    qs.set("limit", "200");
    return `/command-center/logs/recent?${qs.toString()}`;
  }, [service, level, hours]);

  const { data, loading, error } = useApi<RecentLogsResponse>(recentPath, [recentPath]);
  const rates = useApi<ErrorRatesResponse>("/command-center/logs/error-rates");

  const controls = (
    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
      <select value={service} onChange={(e) => setService(e.target.value)}>
        <option value="">All services</option>
        {services.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select value={level} onChange={(e) => setLevel(e.target.value)}>
        <option value="">All levels</option>
        {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
      </select>
      <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
        {HOURS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
      </select>
    </div>
  );

  const description = "Recent application logs from Loki, pinned to the waffy-api namespace.";

  const topApps = (rates.data?.status === "ok" && rates.data.top_apps) || [];

  const errorPills = topApps.length > 0 && (
    <div className="cov-summary">
      {topApps.map((r) => (
        <span key={r.app} className={`cov-pill ${r.errors_1h ? "crit" : "ok"}`}>
          <span className="dot" /><strong>{r.errors_1h}</strong> {r.app}
        </span>
      ))}
    </div>
  );

  if (loading) {
    return (
      <>
        <ContentHeader title="Logs" description={description} right={controls} />
        <div className="loading"><div className="loading-spinner" /><p>Querying Loki…</p></div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <ContentHeader title="Logs" description={description} right={controls} />
        <div className="empty">Could not load logs. Is command-center-api running?</div>
      </>
    );
  }
  if (data.status === "unavailable") {
    return (
      <>
        <ContentHeader title="Logs" description={description} right={controls} />
        <div className="empty">
          Live logs need <code>GRAFANA_TOKEN</code> on the API to query Loki.
          {data.reason && <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--text-muted)" }}>{data.reason}</div>}
        </div>
      </>
    );
  }
  if (data.status === "error") {
    return (
      <>
        <ContentHeader title="Logs" description={description} right={controls} />
        <div className="empty">
          Loki query failed.
          {data.reason && <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--text-muted)" }}>{data.reason}</div>}
        </div>
      </>
    );
  }

  const lines = data.lines; // already newest-first from the API

  return (
    <>
      <ContentHeader title="Logs" count={lines.length} description={description} right={controls} />

      {errorPills}

      {lines.length === 0 ? (
        <div className="empty">No log lines for the selected filters.</div>
      ) : (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            lineHeight: 1.6,
            maxHeight: "calc(100vh - 260px)",
            overflow: "auto",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "10px 14px",
          }}
        >
          {lines.map((l, i) => (
            <div key={`${l.ts}-${i}`} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: lineColor(l.line) }}>
              <span style={{ color: "var(--text-muted)", marginInlineEnd: 10 }}>{fmtTs(l.ts)}</span>
              {l.line}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
